import { useState, useEffect, useMemo } from "react";
import { getClientByID } from "src/api/firebase/clients";
import { getClientId } from "src/app/modules/localstorage";
import Client from "src/app/models/Client";

interface BalanceData {
  amount: number;
  creditLimit?: number;
  currency: string;
  paymentType: string;
}

interface UseClientBalanceReturn {
  balance: BalanceData | null;
  loading: boolean;
  error: string | null;
}

export const useClientBalance = (): UseClientBalanceReturn => {
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        setLoading(true);
        setError(null);

        const clientId = getClientId();
        if (!clientId) {
          setError("No client ID found in storage");
          return;
        }

        const clientData = await getClientByID(clientId);
        if (!clientData) {
          setError("Client not found");
          return;
        }

        setClient(clientData);
      } catch (err) {
        console.error("Error fetching client balance:", err);
        setError(
          err instanceof Error ? err.message : "Failed to fetch client data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, []);

  // Transform client data to balance format
  const balance = useMemo((): BalanceData | null => {
    if (!client) return null;

    return {
      amount: client.balance || 0,
      creditLimit: client.creditLimit,
      currency: "Rp" as const,
      paymentType: client.billingType || "Postpaid",
    };
  }, [client]);

  return {
    balance,
    loading,
    error,
  };
};
